import { XIcon } from "lucide-react";
import { Dialog as DialogPrimitive, Direction } from "radix-ui";
import * as React from "react";

import { useIslandContainer } from "@/contexts/island-container";
import { useLanguage } from "@/contexts/language";
import { cn } from "@/lib/utils";

function Dialog({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) {
  const { dir } = useLanguage();

  return (
    <Direction.Provider dir={dir}>
      <DialogPrimitive.Root data-slot="dialog" {...props} />
    </Direction.Provider>
  );
}

function DialogTrigger({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({
  container,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return (
    <DialogPrimitive.Portal
      data-slot="dialog-portal"
      container={container}
      {...props}
    />
  );
}

function DialogClose({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

function DialogOverlay({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        "tw:fixed tw:inset-0 tw:z-[9999] tw:bg-black/80 tw:data-[state=closed]:animate-out tw:data-[state=closed]:fade-out-0 tw:data-[state=open]:animate-in tw:data-[state=open]:fade-in-0",
        className,
      )}
      {...props}
    />
  );
}

function DialogContent({
  className,
  children,
  overlay = true,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  overlay?: boolean;
}) {
  const { containerRef } = useIslandContainer();

  return (
    <DialogPortal data-slot="dialog-portal" container={containerRef.current}>
      {overlay && <DialogOverlay />}
      <DialogPrimitive.Content
        data-slot="dialog-content"
        className={cn(
          "tw:fixed tw:top-[50%] tw:start-[50%] tw:z-[9999] tw:grid tw:w-full tw:translate-x-[-50%] tw:translate-y-[-50%] tw:gap-6 tw:rounded-lg tw:border tw:bg-background tw:p-6 tw:shadow-lg tw:duration-200 tw:rtl:-translate-x-[-50%] tw:data-[state=closed]:animate-out tw:data-[state=closed]:fade-out-0 tw:data-[state=closed]:zoom-out-95 tw:data-[state=open]:animate-in tw:data-[state=open]:fade-in-0 tw:data-[state=open]:zoom-in-95",
          className,
        )}
        {...props}
      >
        {children}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}

function DialogHeader({
  className,
  children,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-header"
      className={cn("tw:flex tw:flex-col tw:gap-2", className)}
      {...props}
    >
      {children}
    </div>
  );
}

function DialogFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        "tw:flex tw:flex-col-reverse tw:gap-2 tw:sm:flex-row tw:sm:justify-end",
        className,
      )}
      {...props}
    />
  );
}

function DialogTitle({
  className,
  onClose,
  hideClose = false,
  closeDisabled = false,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title> & {
  onClose?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  hideClose?: boolean;
  closeDisabled?: boolean;
}) {
  return (
    <div className="tw:flex tw:w-full tw:flex-row tw:items-center tw:justify-between tw:gap-4">
      <DialogPrimitive.Title
        data-slot="dialog-title"
        className={cn(
          "tw:min-w-0 tw:text-xl tw:leading-none tw:font-semibold tw:tracking-tight",
          className,
        )}
        {...props}
      />
      {!hideClose && (
        <DialogCloseButton onClick={onClose} disabled={closeDisabled} />
      )}
    </div>
  );
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn("tw:text-base tw:text-muted-foreground", className)}
      {...props}
    />
  );
}

function DialogCloseButton({
  className,
  ...props
}: React.ComponentProps<"button">) {
  const { t } = useLanguage();

  return (
    <DialogPrimitive.Close
      autoFocus={false}
      className={cn(
        "tw:inline-flex tw:size-9 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-lg tw:text-muted-foreground tw:opacity-70 tw:transition-opacity tw:hover:bg-muted tw:hover:text-foreground tw:hover:opacity-100 tw:focus:outline-hidden tw:disabled:pointer-events-none tw:[&_svg]:pointer-events-none tw:[&_svg]:shrink-0",
        className,
      )}
      {...props}
    >
      <XIcon className="tw:size-5" />
      <span className="tw:sr-only">{t("Common:Close")}</span>
    </DialogPrimitive.Close>
  );
}

export {
  Dialog,
  DialogClose,
  DialogCloseButton,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
